import React, {useState, useEffect, Fragment} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'
import {Controller, useForm} from 'react-hook-form'
import {joiResolver} from '@hookform/resolvers/joi'
import {AiOutlineInfoCircle} from 'react-icons/ai'
import {FiFileText} from 'react-icons/fi'
import _ from 'lodash'
import {
  VmoButton,
  VmoCard,
  VmoCardContent,
  VmoDatePicker,
  VmoForm,
  VmoFormField,
  VmoFormGroup,
  VmoRadio,
  VmoFormCheckbox,
  VmoFormSelect,
  VmoDropdown,
  VmoTooltip,
} from 'vmo-library'
import {
  ADD_MAINTAINANCE,
  GET_ALL_TEMPLATES,
  GET_SINGLE_MAINTAINANCE,
  UPDATE_MAINTAINANCE,
  GET_SINGLE_TEMPLATES,
  UPDATE_MAINTAINANCE_STATUS,
} from 'actions/types'
import {
  addMaintainance,
  getAllMaintainance,
  getAllServices,
  getAllTemplates,
  getSingleMaintainance,
  getSingleTemplates,
  updateMaintainance,
  updateMaintainanceStatus,
} from 'actions/statuspage'
import LockPermissionTooltip from 'components/common/LockPermissionTooltip'
import {getPathWithPrefix, removeDoubleQuotes} from 'utils/helper'
import {servicesTypes} from 'constants/variables'
import {maintainanceSchema} from 'validation/status-page.schema'
import useUserPermissions from 'hooks/pure/useUserPermission'
import {STATUS_PAGE_PATH} from 'constants/path'
import useApiResponse from 'hooks/impure/useApiResponse'
import PageHeader from 'components/common/PageHeader'
import {Input, TextArea} from 'components/common/Form'
import {useStatusPage} from 'hooks/pure/useStatusPage'
import {STATUS_PAGE_TYPES} from 'constants/statuspage'
import {useUnsavedChanges} from 'hooks/pure/useUnsavedChanges'

const defaultValues = {
  title: '',
  description: '',
  sourceId: '',
  notifySubscribers: false,
  isBackFill: false,
  startDate: new Date(),
  endDate: new Date(),
  startAtSchedule: false,
  endAtSchedule: false,
  advanceOneDay: false,
  advanceOneHour: false,
  atStart: false,
  onCompletion: false,
}

export const CreateMaintainance = ({toggleForm}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {maintenanceId: editId} = useParams()
  const [templateOption, setTemplateOption] = useState([])
  const [serviceId, setServiceId] = useState([])
  const [templateValue, setTemplateValue] = useState(null)
  const [completeMainte, setCompleteMainte] = useState(false)

  const {isLoadingData, type, successLabels = []} = useSelector(state => state.apiReducer)
  const {ServicesList = [], SingalMaintainance = {}} = useSelector(state => state.statuspage)
  const {allServiceByGroup = []} = ServicesList
  const {SingalTemplate = {}} = useSelector(state => state.statuspage)

  const {setWarningModal} = useUnsavedChanges()

  const {
    formState: {errors, isDirty},
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm({
    mode: 'onTouched',
    shouldFocusError: true,
    reValidateMode: 'onChange',
    submitFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(maintainanceSchema),
    defaultValues,
  })

  const {statusPagePermissions} = useUserPermissions()
  const allowManageIncidentAndMaintainanceRole = statusPagePermissions?.['manage_incident_&_maintainance']

  const {sourceOptions} = useStatusPage()

  useApiResponse({
    action: getAllTemplates,
    dependency: [editId],
    enabled: !editId,
    label: GET_ALL_TEMPLATES,
    storePath: 'statuspage.TemplateList',
    onSuccess: TemplateList => {
      const list = TemplateList?.filter(t => t.type === STATUS_PAGE_TYPES.MAINTENANCE)?.map(list => ({
        value: list.id,
        key: list.id,
        text: list.title,
      }))
      setTemplateOption(list || [])
    },
  })

  function updateCheckedService(action, id, idIndex) {
    const list = [...serviceId]

    switch (action) {
      case 'add': {
        list.push({serviceId: id, effect: 'OP'})
        break
      }
      case 'remove': {
        list.splice(idIndex, 1)
        break
      }

      default:
        break
    }

    setServiceId(list)
  }
  function onToggle(id) {
    const serviceIdList = serviceId.map(e => e.serviceId)
    const idIndex = serviceIdList.findIndex(t => t === id)

    if (serviceIdList.indexOf(Number(id)) !== -1) {
      updateCheckedService('remove', id, idIndex)
    } else {
      updateCheckedService('add', id)
    }
  }

  const getValue = id => {
    const valueIndex = serviceId.findIndex(e => e.serviceId === id)
    return serviceId.map(e => (e.serviceId === id ? e.effect : e.effect))[valueIndex]
  }
  useEffect(() => {
    if (isDirty) {
      setWarningModal(true)
    }
    return () => {
      setWarningModal(false)
    }
  }, [isDirty, setWarningModal])

  useEffect(() => {
    dispatch(getAllServices())
    if (editId) dispatch(getSingleMaintainance(editId))
    if (templateValue) dispatch(getSingleTemplates(templateValue))
  }, [dispatch, editId, templateValue])

  useEffect(() => {
    if (successLabels.includes(GET_SINGLE_MAINTAINANCE)) {
      reset(
        _.pick(SingalMaintainance, [
          'title',
          'description',
          'sourceId',
          'notifySubscribers',
          'startAtSchedule',
          'endAtSchedule',
          'isBackFill',
        ])
      )
      setServiceId(
        SingalMaintainance?.services ? SingalMaintainance?.services?.map(e => _.pick(e, ['serviceId', 'effect'])) : []
      )
      setValue('startDate', new Date(SingalMaintainance?.startDate))
      setValue('endDate', new Date(SingalMaintainance?.endDate))
      setValue('advanceOneDay', SingalMaintainance?.sendReminders.advanceOneDay)
      setValue('advanceOneHour', SingalMaintainance?.sendReminders.advanceOneHour)
      setValue('atStart', SingalMaintainance?.sendReminders.atStart)
      setValue('onCompletion', SingalMaintainance?.sendReminders.onCompletion)
    }
    if (successLabels.includes(ADD_MAINTAINANCE)) {
      toggleForm(false)
      dispatch(getAllMaintainance())
    }
    if (successLabels.includes(UPDATE_MAINTAINANCE) || successLabels.includes(UPDATE_MAINTAINANCE_STATUS)) {
      navigate(-1)
      dispatch(getAllServices())
    }
  }, [successLabels, toggleForm, dispatch, reset, navigate, SingalMaintainance, setValue])

  useEffect(() => {
    if (successLabels.includes(GET_SINGLE_TEMPLATES) && templateValue) {
      reset(_.pick(SingalTemplate, ['title', 'description', 'sourceId', 'notifySubscribers']))
      setServiceId(SingalTemplate?.effectedServices)
    }
  }, [successLabels, reset, SingalTemplate, templateValue])

  const submitData = data => {
    const payload = {
      ...data,
      effectedServices: serviceId,
      sendReminders: {
        advanceOneDay: data.advanceOneDay,
        advanceOneHour: data.advanceOneHour,
        atStart: data.atStart,
        onCompletion: data.onCompletion,
      },
    }
    delete payload.advanceOneDay
    delete payload.advanceOneHour
    delete payload.atStart
    delete payload.onCompletion

    if (editId) {
      dispatch(updateMaintainance(editId, payload))
    } else {
      dispatch(addMaintainance(payload))
    }
    setWarningModal(false)
  }
  useEffect(() => {
    if (SingalMaintainance?.maintenanceStatus === 'completed' && editId) setCompleteMainte(true)
    else setCompleteMainte(false)
  }, [editId, SingalMaintainance])

  const getMaintainancebtn = () => {
    if (SingalMaintainance.maintenanceStatus === 'upcoming') {
      return (
        <LockPermissionTooltip isRoleAccessDenied={!allowManageIncidentAndMaintainanceRole}>
          <VmoButton
            className="basic-fill-btn"
            onClick={() =>
              allowManageIncidentAndMaintainanceRole &&
              dispatch(
                updateMaintainanceStatus(editId, {
                  maintenanceStatus: 'start',
                })
              )
            }
          >
            Start Maintenance
          </VmoButton>
        </LockPermissionTooltip>
      )
    }

    if (SingalMaintainance.maintenanceStatus === 'ongoing') {
      return (
        <LockPermissionTooltip isRoleAccessDenied={!allowManageIncidentAndMaintainanceRole}>
          <VmoButton
            className="basic-fill-btn"
            onClick={() =>
              allowManageIncidentAndMaintainanceRole &&
              dispatch(
                updateMaintainanceStatus(editId, {
                  maintenanceStatus: 'complete',
                })
              )
            }
          >
            Complete Maintenance
          </VmoButton>
        </LockPermissionTooltip>
      )
    }
  }

  return (
    <>
      <PageHeader
        title={`${editId ? 'Update' : 'Create'} Maintenance`}
        icon={<FiFileText className="bg-svg" />}
        className="px-0 pb-0 mb-4"
        style={{maxWidth: '617px'}}
      >
        <div className="pageAction">
          {editId && getMaintainancebtn()}

          {!editId && (
            <VmoTooltip
              content="Apply template"
              trigger={
                <VmoDropdown
                  search
                  selection
                  options={templateOption}
                  placeholder="Apply template"
                  onChange={(e, {value}) => setTemplateValue(value)}
                  selectOnBlur={false}
                />
              }
            />
          )}
        </div>
      </PageHeader>

      <div className="setBodyContent">
        <VmoForm className="errorLabel" id="user-form">
          <Input
            control={control}
            name="title"
            label="Maintenance Title"
            placeholder="Maintenance Title"
            maxLength={30}
            required
            disabled={completeMainte}
            fieldProps={{
              style: {maxWidth: '630px'},
            }}
            tooltipProps={{
              content: 'Specify a title for your maintenance',
            }}
          />
          <TextArea
            name="description"
            control={control}
            label="Maintenance Description"
            placeholder="Maintenance Description"
            maxLength={500}
            id="Description"
            disabled={completeMainte}
            fieldProps={{
              style: {maxWidth: '630px'},
            }}
            tooltipProps={{
              content: 'Add a brief description for maintenance',
            }}
          />
          <VmoFormField required style={{maxWidth: '630px'}}>
            <div className="info-header">
              <label>Source</label>
              <VmoTooltip trigger={<AiOutlineInfoCircle />} content="Select source for a particular maintenance" />
            </div>
            <Controller
              name="sourceId"
              render={({field: {value, onChange, onBlur}}) => {
                return (
                  <VmoFormSelect
                    search
                    fluid
                    options={sourceOptions}
                    value={value}
                    disabled={completeMainte}
                    placeholder="Source"
                    onChange={(e, {value}) => onChange(value)}
                    onBlur={onBlur}
                    error={
                      errors.sourceId && {
                        content: removeDoubleQuotes(errors.sourceId.message),
                      }
                    }
                  />
                )
              }}
              control={control}
            />
          </VmoFormField>
          <VmoCard fluid className="switchBox switchBoxSmall">
            <VmoCardContent>
              <div className="d-flex">
                <div>
                  <label>Notification</label>
                  <p className="vmo-lead" style={{width: '100%'}}>
                    Send a Notification subscriber
                  </p>
                </div>

                <Controller
                  control={control}
                  name="notifySubscribers"
                  render={({field: {value, onChange}}) => (
                    <VmoFormCheckbox
                      disabled={completeMainte}
                      toggle
                      checked={value}
                      onChange={(e, {checked}) => onChange(checked)}
                    />
                  )}
                />
              </div>
              <div className="d-flex" style={{margin: '18px 0'}}>
                <div>
                  <label>Back Fill</label>
                  <p className="vmo-lead">Record past maintainance with date and time</p>
                </div>
                <Controller
                  control={control}
                  name="isBackFill"
                  render={({field: {value, onChange}}) => (
                    <VmoFormCheckbox
                      disabled={completeMainte}
                      toggle
                      checked={value}
                      onChange={(e, {checked}) => onChange(checked)}
                    />
                  )}
                />
              </div>
              <VmoFormGroup className="mb-0">
                <VmoFormField required>
                  <div className="info-header">
                    <label>Start Time</label>
                    <VmoTooltip content="Set start time of the maintenance" trigger={<AiOutlineInfoCircle />} />
                  </div>
                  <Controller
                    control={control}
                    name="startDate"
                    render={({field: {value, onChange, onBlur}}) => (
                      <VmoDatePicker
                        className="vmo-picker task-datepicker"
                        value={value}
                        disabled={completeMainte}
                        min={!watch('isBackFill') ? new Date() : null}
                        inputProps={{
                          component: props => <input {...props} readOnly />,
                        }}
                        onChange={value => onChange(value)}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  {errors.startDate && (
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#9F3A38',
                        margin: '5px',
                      }}
                    >
                      {errors.startDate.message}
                    </div>
                  )}
                </VmoFormField>

                <VmoFormField required className="pr-0">
                  <div className="info-header">
                    <label>End Time</label>
                    <VmoTooltip content="Set end time of the maintenance" trigger={<AiOutlineInfoCircle />} />
                  </div>
                  <Controller
                    name="endDate"
                    control={control}
                    render={({field: {value, onChange, onBlur}}) => (
                      <VmoDatePicker
                        className="vmo-picker task-datepicker"
                        min={!watch('isBackFill') ? new Date() : null}
                        value={value}
                        disabled={completeMainte}
                        inputProps={{
                          component: props => <input {...props} readOnly />,
                        }}
                        onChange={value => onChange(value)}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  {errors.endDate && (
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#9F3A38',
                        margin: '5px 0 0',
                      }}
                    >
                      {errors.endDate.message}
                    </div>
                  )}
                </VmoFormField>
              </VmoFormGroup>
              <div className="d-flex" style={{margin: '18px 0'}}>
                <div>
                  <label>At scheduled start time</label>
                  <p>Set service status to under maintenance</p>
                </div>
                <Controller
                  control={control}
                  name="startAtSchedule"
                  render={({field: {value, onChange}}) => (
                    <VmoFormCheckbox
                      toggle
                      disabled={completeMainte}
                      checked={value}
                      onChange={(e, {checked}) => onChange(checked)}
                    />
                  )}
                />
              </div>
              <div className="d-flex" style={{margin: '18px 0 0'}}>
                <div>
                  <label>At scheduled end time</label>
                  <p>Set service status to operational</p>
                </div>
                <Controller
                  control={control}
                  name="endAtSchedule"
                  render={({field: {value, onChange}}) => (
                    <VmoFormCheckbox
                      toggle
                      disabled={completeMainte}
                      checked={value}
                      onChange={(e, {checked}) => onChange(checked)}
                    />
                  )}
                />
              </div>
              {watch('notifySubscribers') && (
                <>
                  <div className="d-flex" style={{margin: '18px 0 0'}}>
                    <div>
                      <h5>Send reminders</h5>
                      <p>1 Day in Advance</p>
                    </div>
                    <Controller
                      control={control}
                      name="advanceOneDay"
                      render={({field: {value, onChange}}) => (
                        <VmoFormCheckbox
                          toggle
                          disabled={completeMainte}
                          checked={value}
                          onChange={(e, {checked}) => onChange(checked)}
                        />
                      )}
                    />
                  </div>
                  <div className="d-flex" style={{margin: '18px 0'}}>
                    <div>
                      <h5>Send reminders</h5>
                      <p>1 hour in Advance</p>
                    </div>
                    <Controller
                      control={control}
                      name="advanceOneHour"
                      render={({field: {value, onChange}}) => (
                        <VmoFormCheckbox
                          toggle
                          disabled={completeMainte}
                          checked={value}
                          onChange={(e, {checked}) => onChange(checked)}
                        />
                      )}
                    />
                  </div>
                  <div className="d-flex">
                    <div>
                      <h5>Send reminders</h5>
                      <p>At Start</p>
                    </div>
                    <Controller
                      control={control}
                      name="atStart"
                      render={({field: {value, onChange}}) => (
                        <VmoFormCheckbox
                          toggle
                          disabled={completeMainte}
                          checked={value}
                          onChange={(e, {checked}) => onChange(checked)}
                        />
                      )}
                    />
                  </div>
                  <div className="d-flex" style={{margin: '18px 0 0'}}>
                    <div>
                      <h5>Send reminders</h5>
                      <p>On Completion</p>
                    </div>
                    <Controller
                      control={control}
                      name="onCompletion"
                      render={({field: {value, onChange}}) => (
                        <VmoFormCheckbox
                          toggle
                          disabled={completeMainte}
                          checked={value}
                          onChange={(e, {checked}) => onChange(checked)}
                        />
                      )}
                    />
                  </div>
                </>
              )}
            </VmoCardContent>
          </VmoCard>

          <VmoCard fluid className="compoCard multi-compoCard select-services switchBoxSmall">
            <VmoCardContent>
              <h4 className="required-content">Select Services</h4>
              {allServiceByGroup
                .filter(service => service.type === 'service')
                .map((service, i) => (
                  <Fragment key={service.id}>
                    <div key={i} className="d-flex mb-0">
                      <div>
                        <label>{service.name}</label>
                        <p className="vmo-lead">Toggle dashboard portal</p>
                      </div>
                      <VmoRadio
                        toggle
                        disabled={completeMainte}
                        checked={serviceId.map(e => e.serviceId).includes(service.id)}
                        onChange={() => onToggle(service.id)}
                      />
                    </div>
                    {serviceId.some(e => e.serviceId === service.id) && (
                      <VmoFormField required width={10} style={{padding: '18px 0 0'}}>
                        <label>Services Types</label>
                        <VmoFormSelect
                          search
                          fluid
                          options={servicesTypes}
                          defaultValue="OP"
                          value={getValue(service.id)}
                          placeholder="Services Types"
                          onChange={(e, {value}) =>
                            setServiceId(prv =>
                              prv.map(el => (el.serviceId === service.id ? {...el, effect: value} : el))
                            )
                          }
                        />
                      </VmoFormField>
                    )}
                  </Fragment>
                ))}
            </VmoCardContent>
          </VmoCard>

          <VmoCard fluid className="compoCard multi-compoCard select-services switchBoxSmall">
            <VmoCardContent>
              {allServiceByGroup
                .filter(service => service.type === 'group')
                .map((group, i) => (
                  <Fragment key={group.id}>
                    <div className="d-flex m-0" key={i}>
                      <div className="info">
                        <label>{group.name}</label>
                      </div>
                    </div>
                    {group?.services?.map((gServices, i) => (
                      <Fragment key={gServices.id}>
                        <div className="d-flex m-0" key={i}>
                          <div>
                            <label>{gServices.name}</label>
                            <p className="vmo-lead">Toggle API services</p>
                          </div>
                          <div className="actBtn">
                            <VmoRadio
                              toggle
                              disabled={completeMainte}
                              checked={serviceId.map(e => e.serviceId).includes(gServices.id)}
                              onChange={() => onToggle(gServices.id)}
                            />
                          </div>
                        </div>
                        {serviceId.some(e => e.serviceId === gServices.id) && (
                          <VmoFormField required width={10} style={{padding: '18px 0 0'}}>
                            <label>Services Types</label>
                            <VmoFormSelect
                              search
                              fluid
                              options={servicesTypes}
                              defaultValue="OP"
                              value={getValue(gServices.id)}
                              disabled={completeMainte}
                              placeholder="Services Types"
                              onChange={(e, {value}) =>
                                setServiceId(prv =>
                                  prv.map(el => (el.serviceId === gServices.id ? {...el, effect: value} : el))
                                )
                              }
                            />
                          </VmoFormField>
                        )}
                      </Fragment>
                    ))}
                  </Fragment>
                ))}
            </VmoCardContent>
          </VmoCard>
        </VmoForm>
      </div>
      <div className="setActionBtn">
        <VmoButton
          basic
          onClick={() => {
            if (editId) {
              navigate(getPathWithPrefix(STATUS_PAGE_PATH.PREFIX, STATUS_PAGE_PATH.MAINTENANCE))
              reset(defaultValues)
            } else {
              toggleForm(false)
              reset(defaultValues)
            }
          }}
          disabled={isLoadingData && type.includes(UPDATE_MAINTAINANCE)}
          loading={isLoadingData && type.includes(UPDATE_MAINTAINANCE)}
        >
          Cancel
        </VmoButton>

        <LockPermissionTooltip isRoleAccessDenied={!allowManageIncidentAndMaintainanceRole}>
          <VmoButton
            primary
            className={!allowManageIncidentAndMaintainanceRole ? 'disabled-button' : ''}
            onClick={allowManageIncidentAndMaintainanceRole && handleSubmit(submitData)}
            disabled={
              !serviceId.length > 0 ||
              !isDirty ||
              (isLoadingData && type.includes(UPDATE_MAINTAINANCE)) ||
              completeMainte
            }
            loading={isLoadingData && type.includes(UPDATE_MAINTAINANCE)}
          >
            {editId ? 'Update' : 'Save'}
          </VmoButton>
        </LockPermissionTooltip>
      </div>
    </>
  )
}
